import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import SmartImage from '../../components/SmartImage';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Edit, Eye, EyeOff, GripVertical, Users } from 'lucide-react';
import { toast } from 'sonner';

const ManageDelegates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [delegates, setDelegates] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDelegate, setEditingDelegate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    position: '',
    image_url: '',
    order: 0,
    is_active: true
  });

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [delegatesRes, mediaRes] = await Promise.all([
        api.get('/delegates/admin/'),
        api.get('/media/')
      ]);
      setDelegates(delegatesRes.data);
      setMediaFiles(mediaRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      country: '',
      position: '',
      image_url: '',
      order: delegates.length,
      is_active: true
    });
    setEditingDelegate(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.country || !formData.image_url) {
      toast.error('Nombre, país e imagen son obligatorios');
      return;
    }

    try {
      if (editingDelegate) {
        await api.put(`/delegates/${editingDelegate.id}/`, formData);
        toast.success('Delegado actualizado');
      } else {
        await api.post('/delegates/', formData);
        toast.success('Delegado creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (delegate) => {
    setEditingDelegate(delegate);
    setFormData({
      name: delegate.name,
      country: delegate.country,
      position: delegate.position || '',
      image_url: delegate.image_url,
      order: delegate.order || 0,
      is_active: delegate.is_active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este delegado?')) return;
    
    try {
      await api.delete(`/delegates/${id}/`);
      toast.success('Delegado eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggle = async (id) => {
    try {
      const res = await api.put(`/delegates/${id}/toggle/`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const countries = [
    'Argentina', 'Bolivia', 'Brasil', 'Chile', 'Colombia', 'Costa Rica', 
    'Cuba', 'Ecuador', 'El Salvador', 'España', 'Estados Unidos', 'Guatemala', 
    'Honduras', 'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú', 
    'Puerto Rico', 'República Dominicana', 'Uruguay', 'Venezuela', 'Otro'
  ];

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="min-h-screen bg-secondary p-6 md:p-8" data-testid="manage-delegates-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary">Gestión de Delegados</h1>
            <p className="text-muted-foreground mt-1">Administra los delegados oficiales de IAASP</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-delegate-btn">
                <Plus className="w-4 h-4 mr-2" />
                Agregar Delegado
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingDelegate ? 'Editar Delegado' : 'Nuevo Delegado'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre Completo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Juan Carlos Pérez"
                    data-testid="delegate-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">País *</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(v) => setFormData({ ...formData, country: v })}
                  >
                    <SelectTrigger data-testid="delegate-country-select">
                      <SelectValue placeholder="Selecciona un país" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Cargo (opcional)</Label>
                  <Input
                    id="position"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Ej: Delegado Regional"
                    data-testid="delegate-position-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Imagen de Credencial *</Label>
                  <Select 
                    value={formData.image_url} 
                    onValueChange={(v) => setFormData({ ...formData, image_url: v })}
                  >
                    <SelectTrigger data-testid="delegate-image-select">
                      <SelectValue placeholder="Selecciona una imagen" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaFiles.filter(m => m.type === 'image').map(m => (
                        <SelectItem key={m.id} value={m.url}>{m.filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Sube las imágenes primero en la Biblioteca de Medios
                  </p>
                </div>

                {formData.image_url && (
                  <div className="border rounded-lg p-4 bg-muted">
                    <Label className="text-xs mb-2 block">Vista previa:</Label>
                    <SmartImage
                      src={formData.image_url}
                      alt="Preview"
                      fallbackLabel="No se pudo cargar"
                      className="max-h-48 mx-auto object-contain rounded"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="order">Orden de aparición</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    min="0"
                    data-testid="delegate-order-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-delegate-btn">
                    {editingDelegate ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Cómo agregar delegados</h3>
                <ol className="text-sm text-blue-800 mt-1 list-decimal list-inside space-y-1">
                  <li>Primero sube la imagen de la credencial en <strong>Biblioteca de Medios</strong></li>
                  <li>Luego haz clic en "Agregar Delegado" y selecciona la imagen</li>
                  <li>Completa el nombre, país y cargo del delegado</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delegates List */}
        {delegates.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Sin delegados</h3>
            <p className="text-muted-foreground mb-4">
              Agrega el primer delegado haciendo clic en el botón de arriba
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {delegates.map((delegate, index) => (
              <Card 
                key={delegate.id} 
                className={`${!delegate.is_active ? 'opacity-60' : ''}`}
                data-testid={`delegate-card-${delegate.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Order Handle */}
                    <div className="text-muted-foreground cursor-move">
                      <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Image */}
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <SmartImage
                        src={delegate.image_url}
                        alt={delegate.name}
                        fallbackLabel={delegate.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{delegate.name}</h3>
                        {!delegate.is_active && (
                          <Badge variant="secondary">Inactivo</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground">{delegate.country}</p>
                      {delegate.position && (
                        <p className="text-sm text-muted-foreground">{delegate.position}</p>
                      )}
                    </div>

                    {/* Order */}
                    <div className="text-center px-4">
                      <span className="text-xs text-muted-foreground block">Orden</span>
                      <span className="font-semibold">{delegate.order}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggle(delegate.id)}
                        title={delegate.is_active ? 'Desactivar' : 'Activar'}
                        data-testid={`toggle-delegate-${delegate.id}`}
                      >
                        {delegate.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(delegate)}
                        data-testid={`edit-delegate-${delegate.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(delegate.id)}
                        data-testid={`delete-delegate-${delegate.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageDelegates;
